import { useRef, useState, type ReactNode } from 'react';
import { Download, FileText } from 'lucide-react';
import DOMPurify from 'dompurify';
import { fetchDocumentObject } from '../lib/portal';
import { track } from '../lib/analytics';

/**
 * Override for form-engine-portable's built-in 'info'/'info_text' field
 * type, registered via the `fieldComponents` extension point (BOP PR #1488 /
 * POSS "Download Template"). The built-in renderer only knows a plain,
 * unauthenticated `window.open(field.downloadUrl)` — this component adds an
 * authenticated Blob-fetch path for application-bound template fields (mirrors
 * ReferenceDetailsStep's viewPdf()), falling back to the vanilla behavior for
 * any other info field so nothing else on the platform changes.
 *
 * Registering an override replaces the ENTIRE built-in rendering for this
 * field type (not just the button), so the markup below deliberately mirrors
 * form-engine-portable's own 'info'/'info_text' renderer (v2.40.7) closely
 * enough that neither applicants nor visual regression checks can tell the
 * difference for fields that don't use a template link. That includes the
 * engine's `presentation` contract (Asana 1217761042299548):
 *
 * - `presentation: 'document'` → undecorated document treatment: no card
 *   background/border/padding, no leading icon, semantic <h3> label, and the
 *   engine's structured-content classes (headings/lists styled by the
 *   engine's own stylesheet, imported globally in main.tsx — no portal CSS).
 * - omitted, 'alert', or any unknown runtime value → the legacy alert card,
 *   byte-for-byte the same markup as before.
 *
 * The value is consumed exactly as authored by BackOffice — no field-name,
 * ID, or step-key inference.
 */
interface InfoField {
  label?: string;
  content?: string;
  description?: string;
  defaultValue?: string;
  downloadUrl?: string;
  downloadLabel?: string;
  referenceTemplateKey?: string;
  /** Authored by BackOffice; engine contract: 'alert' | 'document'. Unknown
   *  runtime values must preserve the legacy alert card. */
  presentation?: string;
}

interface InfoFieldWithTemplateDownloadProps {
  field: InfoField;
}

/**
 * Mirrors the engine's plain-text formatting for description/defaultValue in
 * document mode: `**bold**` spans and hanging indents for `•`/`-` bullet
 * lines. (The engine applies this in both modes; the alert card below keeps
 * its long-standing plain rendering to leave existing behavior untouched.)
 */
function formatPlainLine(line: string, key: number) {
  const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-');
  const nodes: ReactNode[] = [];
  let text = '';
  let i = 0;
  while (i < line.length) {
    if (line.substring(i, i + 2) === '**') {
      if (text) {
        nodes.push(<span key={`text-${i}`}>{text}</span>);
        text = '';
      }
      const close = line.indexOf('**', i + 2);
      if (close !== -1) {
        nodes.push(
          <strong key={`bold-${i}`} className="font-semibold">
            {line.substring(i + 2, close)}
          </strong>,
        );
        i = close + 2;
      } else {
        text += '**';
        i += 2;
      }
    } else {
      text += line[i];
      i++;
    }
  }
  if (text) nodes.push(<span key={`text-${i}`}>{text}</span>);
  return (
    <div
      key={key}
      className="leading-relaxed"
      style={isBullet ? { paddingLeft: '1.25rem', textIndent: '-1rem' } : {}}
    >
      {nodes.length > 0 ? nodes : line}
    </div>
  );
}

function formatPlainText(value: string | undefined) {
  if (!value || typeof value !== 'string') return [];
  return value.split('\n').map((line, i) => formatPlainLine(line, i));
}

type DownloadFailure = 'network' | 'session' | 'unavailable' | 'blocked';

export const TEMPLATE_DOWNLOAD_MESSAGES: Record<DownloadFailure | 'repeated', string> = {
  network: "We couldn't reach the server. Check your internet connection and try again.",
  session: 'Your session has expired. Please sign in again to download the template.',
  unavailable: "We couldn't download this template right now. Please try again in a few minutes.",
  repeated:
    "We still couldn't download this template. Please contact your Alliance Abroad coordinator and we'll send it to you.",
  blocked: 'Your browser blocked the download. Allow downloads for this site and try again.',
};

/** fetchDocumentObject (lib/portal.ts) reports a non-2xx as `document fetch failed: <status>`; portal.test.ts pins that text. */
function failedFetchStatus(e: unknown): number | undefined {
  const status = /^document fetch failed: (\d{3})$/.exec((e as { message?: string } | null)?.message ?? '')?.[1];
  return status === undefined ? undefined : Number(status);
}

function classifyDownloadFailure(e: unknown): { kind: DownloadFailure; status?: number } {
  const status = failedFetchStatus(e);
  if (status !== undefined) {
    return { kind: status === 401 || status === 403 ? 'session' : 'unavailable', status };
  }
  // fetch() rejects with a TypeError when it never got a response.
  if (e instanceof TypeError || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return { kind: 'network' };
  }
  // Anything else (e.g. a 200 that was HTML, not a document) is the file being unavailable.
  return { kind: 'unavailable' };
}

export function InfoFieldWithTemplateDownload({ field }: InfoFieldWithTemplateDownloadProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const unavailableStreak = useRef(0);
  const hasDownloadLink = Boolean(field.downloadUrl && field.downloadLabel);
  // Strict equality, mirroring the engine: omitted/'alert'/unknown → alert card.
  const isDocument = field.presentation === 'document';
  // BackOffice-authored HTML is expected to be trusted, but the portal still
  // sanitizes at the browser boundary so a broken upstream contract cannot
  // turn an info field into an XSS vector.
  const sanitizedContent = field.content ? DOMPurify.sanitize(field.content) : '';

  const downloadTemplate = async (path: string, templateKey: string) => {
    setBusy(true);
    setError(undefined);
    try {
      const { url } = await fetchDocumentObject(path);
      // An anchor click does not depend on window.open surviving the earlier
      // await (Safari/iOS popup rules swallow it), so the file is always delivered.
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateKey}.pdf`;
      a.rel = 'noopener';
      try {
        a.click();
      } catch {
        track('template_download_failed', { reason: 'blocked', templateKey });
        setError(TEMPLATE_DOWNLOAD_MESSAGES.blocked);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      unavailableStreak.current = 0;
    } catch (e) {
      const { kind, status } = classifyDownloadFailure(e);
      unavailableStreak.current = kind === 'unavailable' ? unavailableStreak.current + 1 : 0;
      track('template_download_failed', { reason: kind, status, templateKey });
      setError(
        kind === 'unavailable' && unavailableStreak.current >= 2
          ? TEMPLATE_DOWNLOAD_MESSAGES.repeated
          : TEMPLATE_DOWNLOAD_MESSAGES[kind],
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (busy) return;
    if (field.referenceTemplateKey && field.downloadUrl) {
      await downloadTemplate(field.downloadUrl, field.referenceTemplateKey);
      return;
    }
    if (field.downloadUrl) {
      window.open(field.downloadUrl, '_blank');
    }
  };

  return (
    <div
      data-info-presentation={isDocument ? 'document' : 'alert'}
      className={isDocument ? 'mb-4' : 'mb-4 rounded-lg p-4'}
      style={
        isDocument
          ? undefined
          : {
              backgroundColor: hasDownloadLink ? '#EFF6FF' : '#F9FAFB',
              color: hasDownloadLink ? '#1E40AF' : '#374151',
              border: hasDownloadLink ? '1px solid #BFDBFE' : '1px solid #E5E7EB',
            }
      }
    >
      <div className={isDocument ? '' : 'flex items-start gap-3'}>
        {!isDocument && hasDownloadLink && (
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          {field.label &&
            (isDocument ? (
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 break-words">
                {field.label}
              </h3>
            ) : (
              <div
                className={`font-semibold mb-3 text-base ${hasDownloadLink ? 'text-blue-900' : ''}`}
              >
                {field.label}
              </div>
            ))}
          <div
            className={
              isDocument
                ? 'text-base text-foreground leading-relaxed space-y-3 break-words [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1'
                : `text-sm leading-relaxed ${hasDownloadLink ? 'text-blue-800' : ''}`
            }
          >
            {sanitizedContent ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
            ) : isDocument ? (
              <div>{formatPlainText(field.description || field.defaultValue || '')}</div>
            ) : (
              <div>{field.description || field.defaultValue || ''}</div>
            )}
          </div>
          {hasDownloadLink && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleDownload}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                <Download className="w-4 h-4" />
                {busy ? 'Preparing download…' : field.downloadLabel}
              </button>
              {error && (
                <p role="alert" className="mt-2 text-sm" style={{ color: '#B91C1C' }}>
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
